import BotAlert from '@/components/BotAlert';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import Spinner from '@/components/Spinner';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useUserContext } from '@/contexts/UserProvider';
import { useGetBotData } from '@/hooks/useBot';
import {
  updateBotSetting, getDeliveryPreferences, saveDeliveryPreferences, DeliveryPreferences,
} from '@/services/bots';
import { getHubSpotAuthStatus, getSalesforceAuthStatus } from '@/services/integrations';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { MultiSelect } from '@/components/audiences/shared/MultiSelect';
import {
  COUNTRY_REGION_PRESETS, countryOptions, matchingPresetKeys, normalizeCountryCodes,
} from '@/constants/countryRegions';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  UserPlus, Globe, MessageSquare, Headset, Code2, Copy, Check, Trash2,
} from 'lucide-react';
import {
  DynamicListField, SectionCard, StickyActionBar, ToggleRow, FieldRow,
  ChatbotPreviewPanel, PreviewTabLayout, InfoBanner,
} from './shared';
import {
  CustomFieldsEditor, CustomFieldDef, sanitizeCustomFields,
} from './shared/CustomFieldsEditor';
import { TimezoneSelector } from '@/components/audiences';
import { normalizeRoutePattern, normalizeRoutePatterns } from '@/utils/routePattern';
import DeleteBotDialog from './DeleteBotDialog';

interface ISettingProps {
  token: string;
  botName: string;
  /** Called after the bot is deleted, so the page can reload its bot list. */
  onDeleted?: () => void;
}

// #3626: out-of-hours handoff config. Same per-weekday shape as the backend
// (customizationdata.outOfHours.days) and demo-booking availability.
type BusinessDay = { enabled: boolean; start: string; end: string };

const OUT_OF_HOURS_DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
] as const;

const buildDefaultBusinessHours = (): Record<string, BusinessDay> => ({
  monday: { enabled: true, start: '09:00', end: '17:00' },
  tuesday: { enabled: true, start: '09:00', end: '17:00' },
  wednesday: { enabled: true, start: '09:00', end: '17:00' },
  thursday: { enabled: true, start: '09:00', end: '17:00' },
  friday: { enabled: true, start: '09:00', end: '17:00' },
  saturday: { enabled: false, start: '09:00', end: '17:00' },
  sunday: { enabled: false, start: '09:00', end: '17:00' },
});

const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const Setting: FC<ISettingProps> = ({ token, botName, onDeleted }) => {
  const { vendorConfig } = useUserContext();
  const { data: botData, isLoading: isBotDataLoading } = useGetBotData(token);
  const previewGrant: string | undefined = botData?.previewGrant;
  const queryClient = useQueryClient();
  // Tracks which bot (token) has been hydrated. Switching bots changes the `token` prop
  // without remounting, so a boolean guard would leave the previous bot's data on screen
  // and a Save would clobber the newly-selected bot. Re-hydrate whenever the token changes.
  const initializedToken = useRef<string | null>(null);

  const [name, setName] = useState<string>('Bot');
  const [greetingMessage1, setGreetingMessage1] = useState<string>('Hi! 👋');
  const [greetingMessage2, setGreetingMessage2] = useState<string>('How can we help?');
  const [welcomeMessage, setWelcomeMessage] = useState<string>('Welcome! 👋 What can I help you with today?');
  const [primaryColor, setPrimaryColor] = useState<string>('#3662E3');
  const [chatHeight, setChatHeight] = useState<number>(700);
  const [fontSize, setFontSize] = useState<number>(16);

  const [alert, setAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // #2637 data-loss guard: Save is blocked until this bot's saved config has actually
  // hydrated into the form. Without it, a Save fired before botData loads POSTs empty
  // routes/suppress_urls (wiping Page Targeting) and default customizationdata.
  const [hydrated, setHydrated] = useState(false);
  const [isLoad, setIsLoad] = useState(0);

  const [requireEmailForChat, setRequireEmailForChat] = useState(false);
  const [emailRequestIntensity, setEmailRequestIntensity] = useState('medium');
  const [defaultBotActive, setDefaultBotActive] = useState(false);

  // Install snippet: the embed loader is served per-bot at
  // <NEXT_PUBLIC_HOST_URL>/api/<token>.js. It must be an ABSOLUTE url — the
  // snippet is pasted on the customer's own domain, so a relative /api/... path
  // would resolve against their host, not ours (NEXT_PUBLIC_API_ENDPOINT is a
  // relative "/api" in prod, hence not usable here). Page Targeting only takes
  // effect once this <script> is on the customer's site — a bot can be fully
  // configured here yet never appear because the snippet was never pasted (the
  // exact confusion Pharos hit). Surface it.
  const embedHost = process.env.NEXT_PUBLIC_HOST_URL
    || (typeof window !== 'undefined' ? window.location.origin : '');
  const embedSnippet = `<script src="${embedHost}/api/${token}.js"></script>`;
  const [snippetCopied, setSnippetCopied] = useState(false);
  const copySnippet = () => {
    navigator.clipboard.writeText(embedSnippet).then(() => {
      setSnippetCopied(true);
      setTimeout(() => setSnippetCopied(false), 2000);
    }).catch(() => { /* clipboard unavailable — user can select the text manually */ });
  };

  // Per-bot lead-form configuration. Stored inside customizationdata.leadFormFields. When the
  // key is absent on an existing bot we hydrate to all-true (matches the chatbot widget's
  // LEAD_FORM_FIELDS_DEFAULT, so legacy bots retain the just-shipped UX). Email is always
  // required and the toggle is locked in the UI.
  const [leadFormFields, setLeadFormFields] = useState<{
    firstName: boolean;
    lastName: boolean;
    website: boolean;
    company: boolean;
    email: boolean;
  }>({
    firstName: true, lastName: true, website: true, company: false, email: true,
  });

  // #2866: vendor-defined custom fields shown on the pre-chat form. Stored in
  // customizationdata.customFields and rendered by the widget.
  const [customFields, setCustomFields] = useState<CustomFieldDef[]>([]);

  const [inputPrompts, setInputPrompts] = useState([{ id: 1, value: '' }]);
  const [routes, setRoutePrompts] = useState([{ id: 1, value: '' }]);
  // GH#2373: vendor-managed list of path patterns that suppress the chatbot widget.
  const [suppressUrls, setSuppressUrls] = useState<{ id: number; value: string }[]>([{ id: 1, value: '' }]);
  // mb-server#6239: ISO-3166 alpha-2 codes the widget may render in. Empty = every country,
  // which is the default for every bot.
  const [allowedCountries, setAllowedCountries] = useState<string[]>([]);
  // Built once: ~250 options, each resolving a display name through Intl.
  const countryOptionList = useMemo(() => countryOptions(), []);
  const activePresetKeys = useMemo(() => matchingPresetKeys(allowedCountries), [allowedCountries]);
  const maxInputs = 10;
  const maxRouteInputs = 10;
  const maxSuppressInputs = 50;

  const [supportURL, setSupportRedirectUrl] = useState(false);
  const [supportUrlValue, setInputRedirectURL] = useState('');

  const [enablePrivacyMessage, setEnablePrivacyMessage] = useState(false);
  const [privacyText, setPrivacyText] = useState('');
  const [privacyLink, setPrivacyLink] = useState('');

  const [hideChatbot, setHideChatbot] = useState(false);
  const [hideChatbotOnMobile, setHideChatbotOnMobile] = useState(false);
  const [hideOutsideBusinessHours, setHideOutsideBusinessHours] = useState(false);
  const [pounceMessage, setPounceMessage] = useState<string>('');
  const [onLaunch, setOnLaunch] = useState(false);
  const [disableEngagePopup, setDisableEngagePopup] = useState(false);
  const [showFile, setShowFile] = useState(true);
  const [enableFollowUpSuggestions, setEnableFollowUpSuggestions] = useState(false);
  const [showViewMore, setShowViewMore] = useState(true);
  const [autoNavigateEnabled, setAutoNavigateEnabled] = useState(false);

  // Live preview (right column) — same pattern as Appearance / AI & Persona tabs.
  const previewTokenKey = `mb_preview_uidtoken_${token}`;
  const [previewUserIdToken, setPreviewUserIdToken] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    const existing = window.localStorage.getItem(previewTokenKey);
    if (existing) return existing;
    const fresh = uuidv4();
    window.localStorage.setItem(previewTokenKey, fresh);
    return fresh;
  });
  const [previewNonce, setPreviewNonce] = useState(0);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [isReadyToRenderIframe, setIsReadyToRenderIframe] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isStrictModeStableRef = useRef(false);
  const [disallowFreeEmail, setDisallowFreeEmail] = useState(false);
  const [aiSuggestionsEnabled, setAiSuggestionsEnabled] = useState(false);

  // #3625: vendor-customizable lead/contact form prompt (customizationdata.contactFormMessage).
  const [contactFormMessage, setContactFormMessage] = useState('');
  // #3626: out-of-hours human-handoff config (customizationdata.outOfHours).
  const [outOfHoursEnabled, setOutOfHoursEnabled] = useState(false);
  const [outOfHoursTimezone, setOutOfHoursTimezone] = useState<string>(getBrowserTimezone());
  const [outOfHoursMessage, setOutOfHoursMessage] = useState('');
  const [businessHours, setBusinessHours] = useState<Record<string, BusinessDay>>(buildDefaultBusinessHours());

  // Lead delivery destinations (#2851) — vendor-level (apply to all of this workspace's
  // chatbots), loaded independently of the per-bot settings. Email is always-on; HubSpot/
  // Salesforce are only shown/toggleable when the vendor has that CRM connected.
  const [hubspotConnected, setHubspotConnected] = useState(false);
  const [salesforceConnected, setSalesforceConnected] = useState(false);
  const [deliveryHubspot, setDeliveryHubspot] = useState(false);
  const [deliverySalesforce, setDeliverySalesforce] = useState(false);
  // Only persist delivery prefs once they've loaded, and preserve the saved value for a
  // destination we can't currently render (transient auth-status failure) so a Save never
  // silently removes a working CRM delivery.
  const [deliveryLoaded, setDeliveryLoaded] = useState(false);
  const loadedDelivery = useRef<DeliveryPreferences | null>(null);

  const handleSupportRedirectURL = () => {
    if (supportUrlValue !== '' && supportUrlValue !== null) {
      setSupportRedirectUrl(!supportURL);
    }
  };

  const handleAddInput = () => {
    if (inputPrompts.length < maxInputs) {
      const newId = inputPrompts[inputPrompts.length - 1].id + 1;
      setInputPrompts([...inputPrompts, { id: newId, value: '' }]);
    }
  };

  const handleRouteAddInput = () => {
    if (routes.length < maxRouteInputs) {
      const newId = routes[routes.length - 1].id + 1;
      setRoutePrompts([...routes, { id: newId, value: '' }]);
    }
  };

  const handleInputChange = (id: number, value: string) => {
    setInputPrompts(inputPrompts.map((input) => (input.id === id ? { ...input, value } : input)));
  };

  const handleRouteChange = (id: number, value: string) => {
    setRoutePrompts(routes.map((input) => (input.id === id ? { ...input, value } : input)));
  };

  // #5012: rewrite a pasted address bar to the path the embed actually matches, as soon as
  // the user leaves the field, so "https://site.com/pricing" visibly becomes "/pricing"
  // rather than silently matching nothing.
  const handleRouteBlur = (id: number, value: string) => {
    const normalized = normalizeRoutePattern(value);
    if (normalized === value) return;
    setRoutePrompts(routes.map((input) => (input.id === id ? { ...input, value: normalized } : input)));
  };

  const handleSuppressBlur = (id: number, value: string) => {
    const normalized = normalizeRoutePattern(value);
    if (normalized === value) return;
    setSuppressUrls(suppressUrls.map((input) => (input.id === id ? { ...input, value: normalized } : input)));
  };

  const handleRemoveInput = (id: number) => {
    if (inputPrompts.length > 1) {
      setInputPrompts(inputPrompts.filter((input) => input.id !== id));
    }
  };

  const handleRouteRemoveInput = (id: number) => {
    if (routes.length > 1) {
      setRoutePrompts(routes.filter((input) => input.id !== id));
    }
  };

  const handleSuppressAddInput = () => {
    if (suppressUrls.length < maxSuppressInputs) {
      const newId = suppressUrls[suppressUrls.length - 1].id + 1;
      setSuppressUrls([...suppressUrls, { id: newId, value: '' }]);
    }
  };

  const handleSuppressChange = (id: number, value: string) => {
    setSuppressUrls(suppressUrls.map((input) => (input.id === id ? { ...input, value } : input)));
  };

  const handleSuppressRemoveInput = (id: number) => {
    if (suppressUrls.length > 1) {
      setSuppressUrls(suppressUrls.filter((input) => input.id !== id));
    } else {
      setSuppressUrls([{ id: 1, value: '' }]);
    }
  };

  // #5012: both lists are compared against window.location.pathname by the embed, so both
  // are stored as pathname patterns. The server normalizes again on write — this keeps the
  // form honest about what it is about to save.
  const onParseSuppressUrls = () =>
    normalizeRoutePatterns(suppressUrls.map((s) => s.value));

  const onParseInputPrompts = () => {
    if (!Array.isArray(inputPrompts)) return [];
    return inputPrompts.filter((p) => p.value !== '');
  };

  const onParseInputRoutes = () => normalizeRoutePatterns(routes.map((r) => r.value));

  const onHandleSave = async () => {
    // #2637: never persist an un-hydrated form (would POST empty routes/suppress_urls
    // + default customizationdata over good saved data). Belt to the disabled button.
    if (!hydrated) return;
    const isDefault = defaultBotActive;
    setIsLoading(true);
    try {
      const data = {
        name,
        welcomeMessage,
        primaryColor,
        chatHeight,
        fontSize,
        // botIcon/userIcon are deliberately absent: this form has no icon control, and
        // echoing back a hydrated URL string (or '' when it loaded before the upload)
        // overwrote the { uploadedUri } object the Appearance tab saves, blanking the
        // vendor's logo in the widget on every Settings save.
        inputPrompts: onParseInputPrompts(),
        greetingMessage1,
        greetingMessage2,
        enablePrivacyMessage,
        privacyText,
        privacyLink,
        hideChatbot,
        hideChatbotOnMobile,
        hideOutsideBusinessHours,
        pounceMessage,
        onLaunch,
        disableEngagePopup,
        leadFormFields: { ...leadFormFields, email: true },
        customFields: sanitizeCustomFields(customFields),
        // #3625: blank string falls back to the default copy on the backend.
        contactFormMessage: contactFormMessage.trim(),
        // #3626: out-of-hours handoff config. Feature is off unless enabled is true.
        outOfHours: {
          enabled: outOfHoursEnabled,
          timezone: outOfHoursTimezone,
          message: outOfHoursMessage.trim(),
          days: businessHours,
        },
      };
      const parsedRoutes = onParseInputRoutes();
      await updateBotSetting(
        data,
        token,
        isDefault,
        parsedRoutes,
        supportURL,
        supportUrlValue,
        emailRequestIntensity,
        requireEmailForChat,
        showFile,
        enableFollowUpSuggestions,
        showViewMore,
        autoNavigateEnabled,
        disallowFreeEmail,
        onParseSuppressUrls(),
        aiSuggestionsEnabled,
        allowedCountries,
      );

      // Lead delivery destinations (#2851) — vendor-level, persisted separately. Only send a
      // destination's checkbox value when we could render it (connected); otherwise preserve
      // the saved value so a Save never silently drops a working CRM delivery.
      if (deliveryLoaded) {
        await saveDeliveryPreferences({
          hubspot: hubspotConnected ? deliveryHubspot : (loadedDelivery.current?.hubspot ?? false),
          salesforce: salesforceConnected ? deliverySalesforce : (loadedDelivery.current?.salesforce ?? false),
        });
      }

      await queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getBotData(token), token],
      });

      setIsLoading(false);
      setAlertMessage('Settings saved successfully!');
      setAlert(true);
      setIsLoad(isLoad === 0 ? 1 : 0);
      // Reload preview so behavior toggles (auto-navigate, View More, etc.) reflect saved state.
      setPreviewNonce((n) => n + 1);
    } catch {
      setIsLoading(false);
      setAlertMessage('An error occurred while saving. Please try again.');
      setAlert(true);
    }
  };

  const init = async () => {
    try {
      if (!vendorConfig?.vendorId) return;

      if (botData?.data?.status === true) {
        // Mark this token hydrated only once we actually hydrate. Setting this in the effect
        // (before hydration) meant an early-return here left routes/suppressUrls at their
        // empty defaults, and a subsequent Save would POST [] and wipe the saved lists.
        initializedToken.current = token;
        const d = botData.data;
        const c = d.customizationdata;

        setSupportRedirectUrl(d.is_support ?? false);
        setInputRedirectURL(d.support_link ?? '');

        if (d.routes?.length) {
          setRoutePrompts(d.routes.map((v: string, i: number) => ({ id: i + 1, value: v })));
        } else {
          setRoutePrompts([{ id: 1, value: '' }]);
        }

        // GH#2373: hydrate suppress_urls (top-level chat_bot column).
        const storedSuppress: string[] = Array.isArray(d.suppress_urls) ? d.suppress_urls : [];
        if (storedSuppress.length) {
          setSuppressUrls(storedSuppress.map((v, i) => ({ id: i + 1, value: v })));
        } else {
          setSuppressUrls([{ id: 1, value: '' }]);
        }

        // mb-server#6239: hydrate allowed_countries (top-level chat_bot column). Normalized the
        // same way the backend normalizes on write, so the form shows what is actually stored.
        setAllowedCountries(normalizeCountryCodes(d.allowed_countries));

        setDefaultBotActive(d.is_default);
        setName(c.name);
        setGreetingMessage1(c.greetingMessage1);
        setGreetingMessage2(c.greetingMessage2);
        setWelcomeMessage(c.welcomeMessage);
        setPrimaryColor(c.primaryColor);
        setChatHeight(c.chatHeight);
        setFontSize(c.fontSize);
        setHideChatbot(c.hideChatbot || false);
        setOnLaunch(c.onLaunch || false);
        setDisableEngagePopup(c.disableEngagePopup || false);
        setShowFile(d.show_file !== false);
        setShowViewMore(d.show_view_more !== false);
        setAutoNavigateEnabled(d.auto_navigate_enabled === true);
        setEmailRequestIntensity(d.email_request_intensity ?? 'medium');
        setRequireEmailForChat(d.require_email_for_chat ?? false);
        setDisallowFreeEmail(d.disallow_free_email ?? false);
        setEnableFollowUpSuggestions(d.enable_follow_up_suggestions ?? false);
        setAiSuggestionsEnabled(d.ai_suggestions_enabled ?? false);
        setEnablePrivacyMessage(c.enablePrivacyMessage || false);
        setPrivacyText(c.privacyText || '');
        setPrivacyLink(c.privacyLink || '');
        setHideChatbotOnMobile(c.hideChatbotOnMobile || false);
        setHideOutsideBusinessHours(c.hideOutsideBusinessHours === true);
        setPounceMessage(c.pounceMessage || '');

        // #3625 / #3626: hydrate contact-form copy + out-of-hours config from customizationdata.
        setContactFormMessage(c.contactFormMessage || '');
        const ooh = (c.outOfHours as {
          enabled?: boolean; timezone?: string; message?: string; days?: Record<string, BusinessDay>;
        }) || {};
        setOutOfHoursEnabled(ooh.enabled === true);
        setOutOfHoursTimezone(ooh.timezone || getBrowserTimezone());
        setOutOfHoursMessage(ooh.message || '');
        setBusinessHours(
          ooh.days && typeof ooh.days === 'object'
            ? { ...buildDefaultBusinessHours(), ...ooh.days }
            : buildDefaultBusinessHours(),
        );
        // Hydrate lead-form fields from customizationdata. Missing key → all-true (matches the
        // chatbot widget's LEAD_FORM_FIELDS_DEFAULT). Email is always true regardless of stored value.
        const storedFields = (c.leadFormFields as Partial<typeof leadFormFields> | undefined) ?? {};
        setLeadFormFields({
          firstName: storedFields.firstName ?? true,
          lastName: storedFields.lastName ?? true,
          website: storedFields.website ?? true,
          company: storedFields.company ?? false,
          email: true,
        });

        // #2866: hydrate custom fields (absent on legacy bots → empty).
        setCustomFields(Array.isArray(c.customFields) ? (c.customFields as CustomFieldDef[]) : []);

        if (c.inputPrompts?.length) {
          setInputPrompts(
            c.inputPrompts.map((p: { value: string }, i: number) => ({ id: i + 1, value: p.value || '' })),
          );
        } else {
          setInputPrompts([{ id: 1, value: '' }]);
        }
        // Form now reflects saved config — Save is safe to enable.
        setHydrated(true);
      }
    } catch {
      // silently fail — bot data may not exist yet
    }
  };

  // #2637: the moment the selected bot changes, block Save until its data re-hydrates
  // (otherwise the previous bot's hydrated=true would let an un-hydrated Save through).
  useEffect(() => {
    setHydrated(false);
  }, [token]);

  useEffect(() => {
    // Hydrate whenever the selected bot changes. Guard on accesstoken so we never hydrate
    // the newly-selected bot from the previous bot's (stale) cached data.
    if (token && botData?.data?.accesstoken === token && initializedToken.current !== token) {
      init(); //eslint-disable-line react-hooks/set-state-in-effect
    }
  }, [token, vendorConfig?.vendorId, botData]); //eslint-disable-line react-hooks/exhaustive-deps

  // Load lead delivery destinations + CRM connection status (#2851). Vendor-level, so this is
  // keyed on the vendor rather than the selected bot. Connection status decides which CRM
  // destinations are offered; a transient failure just hides that destination (never disables).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!vendorConfig?.vendorId) return;
      const [prefs, hs, sf] = await Promise.all([
        getDeliveryPreferences(),
        getHubSpotAuthStatus().catch(() => ({ authenticated: false })),
        getSalesforceAuthStatus().catch(() => ({ authenticated: false })),
      ]);
      if (cancelled) return;
      setHubspotConnected(hs?.authenticated === true);
      setSalesforceConnected(sf?.authenticated === true);
      if (prefs) {
        loadedDelivery.current = prefs;
        setDeliveryHubspot(prefs.hubspot);
        setDeliverySalesforce(prefs.salesforce);
        setDeliveryLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [vendorConfig?.vendorId]);

  const demoUrl = useMemo(() => {
    if (!vendorConfig?.vendorId || !token) return '';
    const chatbotUrl = process.env.NEXT_PUBLIC_CHATBOT_URL;
    const urlString = typeof chatbotUrl === 'string' ? chatbotUrl : String(chatbotUrl || '');
    if (urlString && urlString.trim() !== '' && urlString !== 'undefined') {
      const baseUrl = urlString.trim();
      const separator = baseUrl.includes('?') ? '&' : '?';
      // GH#6239: see Customize.tsx — the grant is what lets an admin outside the allowlist
      // preview their own restricted bot, and it is bound to this token and short-lived.
      const grant = previewGrant ? `&previewGrant=${encodeURIComponent(previewGrant)}` : '';
      return `${baseUrl}${separator}token=${encodeURIComponent(token)}&defaultOpenBot=true&preview=true&userIdToken=${encodeURIComponent(previewUserIdToken)}${grant}&_r=${previewNonce}`;
    }
    return '';
  }, [token, vendorConfig?.vendorId, previewUserIdToken, previewNonce, previewGrant]);

  const handleResetPreview = useCallback(() => {
    setIframeLoading(true);
    const fresh = uuidv4();
    if (typeof window !== 'undefined') window.localStorage.setItem(previewTokenKey, fresh);
    setPreviewUserIdToken(fresh);
  }, [previewTokenKey]);

  useEffect(() => {
    if (!isStrictModeStableRef.current) {
      isStrictModeStableRef.current = true;
      setTimeout(() => setIsReadyToRenderIframe(true), 0);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !token) return;
    const existing = window.localStorage.getItem(previewTokenKey);
    if (existing) {
      setPreviewUserIdToken(existing);
    } else {
      const fresh = uuidv4();
      window.localStorage.setItem(previewTokenKey, fresh);
      setPreviewUserIdToken(fresh);
    }
  }, [token, previewTokenKey]);

  useEffect(() => {
    if (demoUrl && demoUrl.trim() !== '') setIframeLoading(true);
  }, [demoUrl]);

  const shouldRenderIframe = !!(
    demoUrl
    && demoUrl.trim() !== ''
    && isReadyToRenderIframe
    && botData?.data
    && hydrated
  );

  return (
    <>
      <PreviewTabLayout
        preview={(
          <ChatbotPreviewPanel
            demoUrl={demoUrl}
            shouldRenderIframe={shouldRenderIframe}
            iframeLoading={iframeLoading}
            chatHeight={chatHeight}
            iframeRef={iframeRef}
            onReset={handleResetPreview}
            onIframeSettled={() => setIframeLoading(false)}
            debug={{
              isReadyToRenderIframe,
              isFetching: isBotDataLoading,
              hasData: !!botData?.data,
              hasToken: !!token,
              vendorId: vendorConfig?.vendorId,
            }}
          />
        )}
        footer={(
          <StickyActionBar
            onReset={() => {
              setHydrated(false);
              initializedToken.current = null;
              void init();
            }}
            onSave={() => { void onHandleSave(); }}
            saving={isLoading}
            resetDisabled={isLoading}
            saveDisabled={isLoading || !hydrated}
          />
        )}
      >
        {/* Two explicit columns on wide screens; stacks to one column below lg. */}
        <div className="grid items-start gap-6 lg:grid-cols-2">
          {/* Left column: Lead Capture + Support & Handoff */}
          <div className="space-y-6">

        {/* Lead Capture */}
        <SectionCard
          icon={UserPlus}
          title="Lead Capture & Consent"
          subtitle="Collect visitor contact details and show a consent notice before chatting."
          bodyClassName="px-6"
        >
          <div className="divide-y divide-gray-100">
            <ToggleRow
              label="Require email to chat"
              description="Visitors must provide their email before starting a conversation."
              checked={requireEmailForChat}
              onCheckedChange={setRequireEmailForChat}
            />

            <FieldRow
              label="Email request intensity"
              description="How assertively the chatbot asks for an email address during conversations."
            >
              <RadioGroup
                value={emailRequestIntensity}
                onValueChange={setEmailRequestIntensity}
                className="flex flex-wrap gap-x-6 gap-y-3"
              >
                {[
                  { value: 'low', label: 'Low', hint: 'Gentle, minimal pressure' },
                  { value: 'medium', label: 'Medium', hint: 'Balanced, professional' },
                  { value: 'high', label: 'High', hint: 'Direct, assertive' },
                ].map(({ value, label, hint }) => (
                  <label
                    key={value}
                    htmlFor={`intensity-${value}`}
                    className="flex cursor-pointer items-start gap-2"
                  >
                    <RadioGroupItem value={value} id={`intensity-${value}`} className="mt-0.5" />
                    <div>
                      <p className="text-sm font-medium capitalize text-gray-800">{label}</p>
                      <p className="text-xs text-gray-500">{hint}</p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </FieldRow>

            <FieldRow
              label="Contact form message"
              description="Shown above the form when the bot asks for contact details. Leave blank to use the default."
            >
              <Input
                id="contact-form-message"
                placeholder="Hi! Please complete the form below, and a team member will reach out to assist you shortly."
                value={contactFormMessage}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContactFormMessage(e.target.value)}
                maxLength={300}
              />
            </FieldRow>

            <FieldRow
              label="Lead form fields"
              description="Fields shown on the pre-chat form. Email is always required."
            >
              <div className="flex flex-wrap gap-x-6 gap-y-2.5">
                <div className="flex items-center gap-2 opacity-50">
                  <Checkbox id="lff-email" checked disabled />
                  <Label htmlFor="lff-email" className="cursor-not-allowed text-sm">
                    Email <span className="text-xs text-gray-400">(required)</span>
                  </Label>
                </div>
                {(
                  [
                    { key: 'firstName', label: 'First Name' },
                    { key: 'lastName', label: 'Last Name' },
                    { key: 'website', label: 'Website' },
                    { key: 'company', label: 'Company' },
                  ] as const
                ).map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-2">
                    <Checkbox
                      id={`lff-${key}`}
                      checked={leadFormFields[key]}
                      onCheckedChange={(checked) =>
                        setLeadFormFields((s) => ({ ...s, [key]: checked === true }))
                      }
                    />
                    <Label htmlFor={`lff-${key}`} className="cursor-pointer text-sm">
                      {label}
                    </Label>
                  </div>
                ))}
              </div>
            </FieldRow>

            <FieldRow
              label="Custom fields"
              description="Extra questions on the pre-chat form. For multiple-choice options you can attach an AI prompt that the bot follows when the visitor picks that option."
            >
              <CustomFieldsEditor fields={customFields} onChange={setCustomFields} />
            </FieldRow>

            <ToggleRow
              label="Block personal email providers"
              description="Reject Gmail, Yahoo, Outlook, etc. and require a business email address."
              checked={disallowFreeEmail}
              onCheckedChange={(checked) => setDisallowFreeEmail(checked === true)}
            />

            <FieldRow
              label="Lead delivery destinations"
              description="Where captured leads are delivered. Applies to all of this workspace's chatbots. Email is always on; connect a CRM under Integrations to deliver there too."
            >
              <div className="flex flex-wrap gap-x-6 gap-y-2.5">
                <div className="flex items-center gap-2 opacity-50">
                  <Checkbox id="dest-email" checked disabled />
                  <Label htmlFor="dest-email" className="cursor-not-allowed text-sm">
                    Email <span className="text-xs text-gray-400">(always)</span>
                  </Label>
                </div>
                {hubspotConnected && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="dest-hubspot"
                      checked={deliveryHubspot}
                      onCheckedChange={(checked) => setDeliveryHubspot(checked === true)}
                    />
                    <Label htmlFor="dest-hubspot" className="cursor-pointer text-sm">
                      HubSpot
                    </Label>
                  </div>
                )}
                {salesforceConnected && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="dest-salesforce"
                      checked={deliverySalesforce}
                      onCheckedChange={(checked) => setDeliverySalesforce(checked === true)}
                    />
                    <Label htmlFor="dest-salesforce" className="cursor-pointer text-sm">
                      Salesforce
                    </Label>
                  </div>
                )}
              </div>
              {!hubspotConnected && !salesforceConnected && (
                <p className="mt-2 text-xs text-gray-400">
                  Connect HubSpot or Salesforce under Integrations to deliver leads to your CRM.
                </p>
              )}
            </FieldRow>

            <ToggleRow
              label="Show privacy notice"
              description="Display a consent message on the visitor's first interaction."
              checked={enablePrivacyMessage}
              onCheckedChange={setEnablePrivacyMessage}
            />

            {enablePrivacyMessage && (
              <>
                <FieldRow label="Privacy message">
                  <textarea
                    id="privacyText"
                    value={privacyText}
                    onChange={(e) => setPrivacyText(e.target.value)}
                    placeholder="By messaging, you agree this chat may be monitored and used for analytics. View our Privacy Policy."
                    className="w-full resize-y rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    rows={3}
                    maxLength={500}
                  />
                  <p className="mt-1 text-right text-xs text-gray-400">{privacyText.length}/500</p>
                </FieldRow>
                <FieldRow label="Privacy policy URL">
                  <Input
                    id="privacyLink"
                    placeholder="https://yourcompany.com/privacy-policy"
                    type="url"
                    value={privacyLink}
                    onChange={(e) => setPrivacyLink(e.target.value)}
                  />
                </FieldRow>
              </>
            )}
          </div>
        </SectionCard>

        {/* Support & Handoff (bottom of left column) */}
        <SectionCard
          icon={Headset}
          title="Support & Handoff"
          subtitle="Route support requests and set how a live agent joins the conversation."
          bodyClassName="px-6"
        >
          <div className="divide-y divide-gray-100">
            <ToggleRow
              label="Redirect support queries"
              description="The AI detects support intent and redirects visitors to the URL below."
              checked={supportURL}
              onCheckedChange={handleSupportRedirectURL}
            />
            <FieldRow label="Support URL">
              <Input
                placeholder="https://yourcompany.com/support"
                type="text"
                value={supportUrlValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputRedirectURL(e.target.value)}
              />
            </FieldRow>
            <FieldRow
              label="Pounce message"
              description="The first message sent automatically when a salesperson joins via Microsoft Teams or Slack. Requires the MarketBetter bot installed in your workspace."
            >
              <Input
                id="pounce-message"
                placeholder="Hi, this is [Name] and I am a real person 😃. Can I assist you today?"
                value={pounceMessage}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPounceMessage(e.target.value)}
              />
            </FieldRow>

            <ToggleRow
              label="Out-of-hours handoff message"
              description="When a visitor asks for a person outside your working hours, tell them the team is offline and will follow up, instead of trying to connect them live."
              checked={outOfHoursEnabled}
              onCheckedChange={setOutOfHoursEnabled}
            />
            {outOfHoursEnabled && (
              <>
                <FieldRow label="Business timezone">
                  <TimezoneSelector value={outOfHoursTimezone} onChange={setOutOfHoursTimezone} />
                </FieldRow>
                <FieldRow
                  label="Working hours"
                  description="Days and hours your team is available for live handoff."
                >
                  <div className="space-y-2">
                    {OUT_OF_HOURS_DAYS.map(({ key, label }) => (
                      <div key={key} className="flex flex-wrap items-center gap-x-3 gap-y-2">
                        <div className="flex w-28 items-center gap-2">
                          <Checkbox
                            id={`ooh-${key}`}
                            checked={businessHours[key]?.enabled ?? false}
                            onCheckedChange={(checked) =>
                              setBusinessHours((s) => ({ ...s, [key]: { ...s[key], enabled: checked === true } }))
                            }
                          />
                          <Label htmlFor={`ooh-${key}`} className="cursor-pointer text-sm">{label}</Label>
                        </div>
                        <Input
                          type="time"
                          className="w-28"
                          value={businessHours[key]?.start ?? '09:00'}
                          disabled={!businessHours[key]?.enabled}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setBusinessHours((s) => ({ ...s, [key]: { ...s[key], start: e.target.value } }))
                          }
                        />
                        <span className="text-sm text-gray-400">to</span>
                        <Input
                          type="time"
                          className="w-28"
                          value={businessHours[key]?.end ?? '17:00'}
                          disabled={!businessHours[key]?.enabled}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setBusinessHours((s) => ({ ...s, [key]: { ...s[key], end: e.target.value } }))
                          }
                        />
                      </div>
                    ))}
                  </div>
                </FieldRow>
                {(() => {
                  const enabledDays = OUT_OF_HOURS_DAYS.filter(({ key }) => businessHours[key]?.enabled);
                  // 'HH:MM' 24h strings compare correctly lexically.
                  const invalid = enabledDays.filter(({ key }) => (businessHours[key]?.start ?? '') >= (businessHours[key]?.end ?? ''));
                  if (enabledDays.length === 0) {
                    return (
                      <p className="text-xs text-amber-600">
                        No working days are enabled, so visitors asking for a person will always see the out-of-hours message and will never be connected live.
                      </p>
                    );
                  }
                  if (invalid.length > 0) {
                    return (
                      <p className="text-xs text-amber-600">
                        {`Check the hours for ${invalid.map((d) => d.label).join(', ')}: the start time must be before the end time, otherwise that day counts as fully out-of-hours.`}
                      </p>
                    );
                  }
                  return null;
                })()}
                <FieldRow
                  label="Out-of-hours message"
                  description="Shown when a visitor asks for a person outside your working hours. Leave blank to use the default below."
                >
                  <textarea
                    id="ooh-message"
                    value={outOfHoursMessage}
                    onChange={(e) => setOutOfHoursMessage(e.target.value)}
                    placeholder="Thanks for reaching out. Our team is currently outside of working hours, so no one is available to connect live right now. We have noted your request and someone will get back to you as soon as we are back online."
                    className="w-full resize-y rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    rows={3}
                    maxLength={500}
                  />
                  <p className="mt-1 text-right text-xs text-gray-400">{outOfHoursMessage.length}/500</p>
                </FieldRow>
              </>
            )}
          </div>
        </SectionCard>
          </div>

          {/* Right column: Page Targeting + Chat Behavior */}
          <div className="space-y-6">

        {/* Page Targeting */}
        <SectionCard
          icon={Globe}
          title="Page Targeting"
          subtitle="Control which pages this bot appears on."
          bodyClassName="px-6"
        >
          <div className="divide-y divide-gray-100">
            <ToggleRow
              label={<>Make <span className="text-primary">{botName}</span> the default bot</>}
              description="Active across the entire website. Route-specific bots take priority over this."
              checked={defaultBotActive}
              onCheckedChange={setDefaultBotActive}
            />

            {!defaultBotActive && (
              <FieldRow
                label="Show on these pages"
                description="URL patterns where this bot appears. Signals are collected from all pages regardless."
              >
                <DynamicListField
                  items={routes}
                  onChange={handleRouteChange}
                  onBlur={handleRouteBlur}
                  onAdd={handleRouteAddInput}
                  onRemove={handleRouteRemoveInput}
                  canAdd={routes.length < maxRouteInputs}
                  addLabel="+ Add Route"
                  placeholder="/products/*"
                  patternLabel="Pattern"
                />
              </FieldRow>
            )}

            <FieldRow
              label="Hide on these pages"
              description={(
                <>
                  Path patterns where the widget is suppressed (e.g. checkout, thank-you pages).{' '}
                  <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">/checkout</code> also hides{' '}
                  <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">/checkout/step-2</code>.
                </>
              )}
            >
              <DynamicListField
                items={suppressUrls}
                onChange={handleSuppressChange}
                onBlur={handleSuppressBlur}
                onAdd={handleSuppressAddInput}
                onRemove={handleSuppressRemoveInput}
                canAdd={suppressUrls.length < maxSuppressInputs}
                addLabel="+ Add Page"
                placeholder="/checkout"
                maxLength={255}
                patternLabel="Pattern"
                showRemove={(item, length) => length > 1 || item.value !== ''}
              />
            </FieldRow>

            {/* mb-server#6239. Deliberately worded around what it does NOT do: the first
                question every operator asks is whether this also stops their visitor data,
                and the answer is no. */}
            <FieldRow
              label="Show chatbot only in these countries"
              description="Leave empty to show the widget everywhere. Website visitor tracking still runs everywhere either way — only the chat widget is hidden. Visitors whose country cannot be determined are hidden."
            >
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  {COUNTRY_REGION_PRESETS.map((preset) => {
                    const active = activePresetKeys.includes(preset.key);
                    return (
                      <button
                        key={preset.key}
                        type="button"
                        onClick={() => setAllowedCountries([...preset.countries])}
                        className={cn(
                          'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                          active
                            ? 'border-brand bg-brand/10 text-brand'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50',
                        )}
                      >
                        {preset.label}
                      </button>
                    );
                  })}
                  {allowedCountries.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setAllowedCountries([])}
                      className="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <MultiSelect
                  options={countryOptionList}
                  value={allowedCountries}
                  onChange={(next) => setAllowedCountries(normalizeCountryCodes(next))}
                  ariaLabel="Countries where the chatbot may appear"
                  placeholder="All countries"
                />
                <p className="text-xs text-gray-500">
                  {allowedCountries.length === 0
                    ? 'Showing in all countries.'
                    : `Showing in ${allowedCountries.length} ${allowedCountries.length === 1 ? 'country' : 'countries'}.`}
                </p>
              </div>
            </FieldRow>

          </div>
        </SectionCard>

        {/* Install — the loader must be on the customer's site for any of the
            Page Targeting above to take effect. Placed right after it so a bot
            can't be configured without seeing how to install it. */}
        <SectionCard
          icon={Code2}
          title="Install on your website"
          subtitle="Paste this once before the closing </body> tag. Add it to every page you want the bot on (or site-wide — it self-limits to the pages targeted above)."
        >
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <code className="min-w-0 flex-1 break-all font-mono text-xs leading-relaxed text-gray-800">
                {embedSnippet}
              </code>
              <button
                type="button"
                onClick={copySnippet}
                className={cn(
                  'flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors',
                  snippetCopied
                    ? 'border-green-200 bg-green-50 text-green-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-100',
                )}
                aria-label="Copy install snippet"
              >
                {snippetCopied
                  ? <><Check className="h-3.5 w-3.5" /> Copied</>
                  : <><Copy className="h-3.5 w-3.5" /> Copy</>}
              </button>
            </div>
            {!defaultBotActive && (
              <p className="text-xs text-gray-500">
                This bot only appears on the pages set under <span className="font-medium">Show on these pages</span> above.
                It won&apos;t show anywhere until this snippet is installed.
              </p>
            )}
          </div>
        </SectionCard>

        {/* 3. Chat Behavior */}
        <SectionCard
          icon={MessageSquare}
          title="Chat Behavior"
          subtitle="How the widget displays and engages visitors, including AI suggestions."
          bodyClassName="px-6"
        >
          <div className="divide-y divide-gray-100">
            <ToggleRow
              label="Hide chat widget"
              description="Widget is hidden but still collects visitor signals from all pages."
              checked={hideChatbot}
              onCheckedChange={setHideChatbot}
            />
            <ToggleRow
              label="Hide on mobile"
              description="Widget is hidden on mobile devices while remaining visible on desktop."
              checked={hideChatbotOnMobile && !hideChatbot}
              onCheckedChange={(checked) => { if (!hideChatbot) setHideChatbotOnMobile(checked); }}
              disabled={hideChatbot}
              className={cn(hideChatbot && 'opacity-40')}
            />
            <ToggleRow
              label="Hide outside business hours"
              description="Widget follows the business timezone and weekly hours configured under Support & Handoff."
              checked={hideOutsideBusinessHours && !hideChatbot}
              onCheckedChange={(checked) => {
                if (!hideChatbot) setHideOutsideBusinessHours(checked === true);
              }}
              disabled={hideChatbot}
              className={cn(hideChatbot && 'opacity-40')}
            />
            <ToggleRow
              label="Auto-open on page load"
              description="The chatbot opens automatically when a visitor loads any page."
              checked={onLaunch}
              onCheckedChange={setOnLaunch}
            />
            <ToggleRow
              label="Disable proactive greeting popup"
              description="Hides the greeting popup that appears automatically above the launcher. The launcher icon stays visible and visitors can still open the chat."
              checked={disableEngagePopup}
              onCheckedChange={setDisableEngagePopup}
            />
            <ToggleRow
              label="Show file attachments"
              description="Display PDF and file links from AI responses to visitors."
              checked={showFile}
              onCheckedChange={setShowFile}
            />
            <ToggleRow
              label='Show "View More" button'
              description="Show an external link button in AI responses for further reading."
              checked={showViewMore}
              onCheckedChange={setShowViewMore}
            />
            <ToggleRow
              label="Auto-navigate to linked pages"
              description={
                showViewMore
                  ? 'When the bot references a page on your site, redirect the visitor there after a short pause (Qualified-style). For that turn, the "View More" button is hidden so visitors are not offered two links. Only same-site pages work. Off by default.'
                  : 'When the bot references a page on your site, redirect the visitor there after a short pause (Qualified-style). Only same-site pages work. Off by default.'
              }
              checked={autoNavigateEnabled}
              onCheckedChange={setAutoNavigateEnabled}
            />
            {autoNavigateEnabled && (
              <div className="pb-4">
                <InfoBanner title="Test in the live preview">
                  Save your changes, then ask the preview bot to visit a page on your site
                  (for example, &ldquo;Take me to pricing&rdquo;). The preview shows a toast
                  instead of redirecting; on your live site visitors are redirected automatically.
                </InfoBanner>
              </div>
            )}
            <ToggleRow
              label='"Ask about" follow-up suggestions'
              description={'Shows suggested follow-up questions under each answer, headed "Ask about". Turn off to let the bot drive the conversation on its own.'}
              checked={enableFollowUpSuggestions}
              onCheckedChange={setEnableFollowUpSuggestions}
            />
            <ToggleRow
              label="AI-generated page welcome"
              description="Shows AI-crafted welcome messages and prompt chips based on the visitor's current page. When off, the configured welcome message is used."
              checked={aiSuggestionsEnabled}
              onCheckedChange={(checked) => setAiSuggestionsEnabled(checked === true)}
            />
            <FieldRow
              label="Engagement prompts"
              description="Suggested starter prompts shown to visitors to kick off a conversation."
            >
              <DynamicListField
                items={inputPrompts}
                onChange={handleInputChange}
                onAdd={handleAddInput}
                onRemove={handleRemoveInput}
                canAdd={inputPrompts.length < maxInputs}
                addLabel="+ Add Prompt"
                placeholder="Select or enter a prompt here."
              />
            </FieldRow>
          </div>
        </SectionCard>

        {/* 4. Delete — the only place a bot can be removed (mb-server#6280) */}
        {onDeleted && (
          <SectionCard
            icon={Trash2}
            title="Delete this bot"
            subtitle="Permanently remove the bot, its training data and its embed. This cannot be undone."
            bodyClassName="px-6 py-4"
          >
            <DeleteBotDialog token={token} botName={botName} onDeleted={onDeleted} />
          </SectionCard>
        )}

          </div>
        </div>
      </PreviewTabLayout>

      <BotAlert message={alertMessage} show={alert} setShow={setAlert} />
    </>
  );
};

export default Setting;
